<template>
    <v-skeleton-loader v-if="isLoading" type="text@2" />
    <div v-else-if="channelManagers.length">
        <v-expansion-panels multiple>
            <v-expansion-panel
                v-for="cm in channelManagers"
                :key="cm.code"
            >
                <v-expansion-panel-title>
                    <span class="font-weight-bold text-subtitle-1">
                        {{ $t(`channelManagersLabel.${cm.code}`) }}
                    </span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                    <v-data-table
                        :headers="headers"
                        :items="mappingData[cm.code] || []"
                        :items-per-page="-1"
                        density="compact"
                        hide-default-footer
                    >
                        <template #item.tariffName="{ item }">
                            {{ tariffMap.get(item.tariffId) || item.tariffId }}
                        </template>
                        <template #item.channelManagerExternalId="{ item }">
                            <v-text-field
                                v-model="item.channelManagerExternalId"
                                :disabled="!editingCms.has(cm.code)"
                                variant="outlined"
                                density="compact"
                                hide-details
                                class="ma-1"
                            />
                        </template>
                    </v-data-table>
                    <div class="d-flex ga-2 mt-2">
                        <v-btn
                            v-if="!editingCms.has(cm.code)"
                            size="small"
                            variant="outlined"
                            @click="startEdit(cm.code)"
                        >
                            {{ $t('edit') }}
                        </v-btn>
                        <template v-else>
                            <v-btn
                                size="small"
                                variant="outlined"
                                color="primary"
                                :loading="isSaving === cm.code"
                                @click="save(cm.code)"
                            >
                                {{ $t('save') }}
                            </v-btn>
                            <v-btn
                                size="small"
                                variant="outlined"
                                @click="cancelEdit(cm.code)"
                            >
                                {{ $t('cancel') }}
                            </v-btn>
                        </template>
                    </div>
                </v-expansion-panel-text>
            </v-expansion-panel>
        </v-expansion-panels>
    </div>
    <div v-else>
        {{ $t('no_data') }}
    </div>
</template>

<script setup lang="ts">
    interface MappingLine {
        tariffId: number
        mealTypeId: number | null
        channelManagerExternalId: string
    }

    const props = defineProps<{
        contractId: number
        productId: number
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()

    const isLoading = ref(true)
    const isSaving = ref<string | null>(null)
    const channelManagers = ref<{code: string; name: string}[]>([])
    const tariffMap = ref<Map<number, string>>(new Map())
    const mappingData = ref<Record<string, MappingLine[]>>({})
    const editSnapshot = ref<Record<string, MappingLine[]>>({})
    const editingCms = ref<Set<string>>(new Set())

    const headers = [
        {title: t('tariff'), key: 'tariffName', sortable: true},
        {title: t('channel_manager_tariff_id'), key: 'channelManagerExternalId', sortable: false},
    ]

    function startEdit(cmCode: string) {
        editingCms.value = new Set([...editingCms.value, cmCode])
        editSnapshot.value[cmCode] = JSON.parse(JSON.stringify(mappingData.value[cmCode] || []))
    }

    function cancelEdit(cmCode: string) {
        mappingData.value[cmCode] = editSnapshot.value[cmCode] || []
        const next = new Set(editingCms.value)
        next.delete(cmCode)
        editingCms.value = next
    }

    async function save(cmCode: string) {
        isSaving.value = cmCode
        try {
            const lines = mappingData.value[cmCode] || []
            await app.$api.channelManagerSynchronization.saveMappingTable({
                productId: props.productId,
                channelManager: cmCode,
                tariffData: lines,
            })
            const next = new Set(editingCms.value)
            next.delete(cmCode)
            editingCms.value = next
            delete editSnapshot.value[cmCode]
            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            console.error('Failed to save mapping table:', e)
            app.$toast.error(e?.msg || t('error_message.save_error'))
        } finally {
            isSaving.value = null
        }
    }

    async function loadData() {
        isLoading.value = true
        try {
            const [cmRs, tariffRs] = await Promise.all([
                app.$api.channelManagers.get(),
                app.$api.tariffs.get({contractId: props.contractId, active: true}),
            ])

            channelManagers.value = cmRs?.channelManagers || cmRs || []

            const tariffs = tariffRs?.tariffs || []
            tariffMap.value = new Map(tariffs.map((t: any) => [t.id, t.name]))

            const mappingPromises = channelManagers.value.map(async cm => {
                try {
                    const rs = await app.$api.channelManagerSynchronization.getMappingTable(
                        props.productId,
                        cm.code,
                    )
                    mappingData.value[cm.code] = rs?.lines || []
                } catch {
                    mappingData.value[cm.code] = []
                }
            })

            await Promise.all(mappingPromises)

            // Merge: add tariffs that exist but aren't in mapping yet
            for (const cm of channelManagers.value) {
                const existingIds = new Set(
                    (mappingData.value[cm.code] || []).map(l => l.tariffId),
                )
                for (const [id] of tariffMap.value) {
                    if (!existingIds.has(id)) {
                        if (!mappingData.value[cm.code]) mappingData.value[cm.code] = []
                        mappingData.value[cm.code].push({
                            tariffId: id,
                            mealTypeId: null,
                            channelManagerExternalId: '',
                        })
                    }
                }
            }
        } catch (e) {
            console.error('Failed to load mapping table data:', e)
        } finally {
            isLoading.value = false
        }
    }

    watch(() => [props.contractId, props.productId], () => {
        if (props.contractId && props.productId) loadData()
    }, {immediate: true})
</script>
